import React, { useEffect, useState } from "react";
import { Table } from "antd";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../../../api";
import DeleteModal from "../../../components/modelpopup/DeleteModal";
import { MODULE_CATALOG } from "../../../utils/permissions";

const emptyForm = { name: "", description: "", modules: [] };

const RolesTable = () => {
  const [data, setData] = useState([]);
  // Start from the bundled catalogue so checkboxes always render; the API
  // (if reachable) refreshes it to stay in sync with the backend.
  const [catalog, setCatalog] = useState(MODULE_CATALOG); // [{key,label}]
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await api.post("/role");
      setData(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching roles:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCatalog = async () => {
    try {
      const response = await api.post("/modules");
      const list = response.data?.data;
      if (Array.isArray(list) && list.length > 0) {
        setCatalog(list);
      }
      // else keep the bundled MODULE_CATALOG fallback
    } catch (error) {
      console.error("Error fetching modules (using bundled catalogue):", error);
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchCatalog();
  }, []);

  const labelForKey = (key) =>
    catalog.find((m) => m.key === key)?.label || key;

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (role) => {
    setEditingId(role.id);
    setForm({
      name: role.name || "",
      description: role.description || "",
      modules: Array.isArray(role.modules) ? role.modules : [],
    });
    setShowModal(true);
  };

  const toggleModule = (key) => {
    setForm((prev) => ({
      ...prev,
      modules: prev.modules.includes(key)
        ? prev.modules.filter((k) => k !== key)
        : [...prev.modules, key],
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Role name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description,
        modules: form.modules,
      };
      if (editingId) {
        await api.post(`/edit-role/${editingId}`, payload);
        toast.success("Role updated successfully");
      } else {
        await api.post("/add-role", payload);
        toast.success("Role created successfully");
      }
      setShowModal(false);
      fetchRoles();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Something went wrong while saving the role"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (selectedId) {
        await api.post(`/delete-role/${selectedId}`);
        toast.success("Role deleted successfully");
        fetchRoles();
        setDeleteModalVisible(false);
      }
    } catch (error) {
      toast.error("Something went wrong while deleting the role");
    }
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
    },
    {
      title: "Description",
      dataIndex: "description",
      render: (text) => text || "-",
    },
    {
      title: "Modules",
      dataIndex: "modules",
      render: (modules, record) => {
        if (record.id === 1) {
          return <span className="badge bg-inverse-danger">All (Super Admin)</span>;
        }
        // Self-service role: access is keyed on the "Employee" name, not modules.
        if (record.name === "Employee") {
          return (
            <span className="badge bg-inverse-success">Self-service (own data)</span>
          );
        }
        if (!Array.isArray(modules) || modules.length === 0) {
          return <span className="text-muted">No access</span>;
        }
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {modules.map((key) => (
              <span key={key} className="badge bg-inverse-info">
                {labelForKey(key)}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      title: "Action",
      render: (text, record) => (
        <div className="dropdown dropdown-action text-end">
          <button
            type="button"
            className="btn btn-sm btn-outline-primary me-2"
            onClick={() => openEdit(record)}
          >
            <i className="fa fa-pencil" /> Edit
          </button>
          {record.id !== 1 && (
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={() => {
                setSelectedId(record.id);
                setDeleteModalVisible(true);
              }}
            >
              <i className="fa fa-trash" /> Delete
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="d-flex justify-content-end mb-3">
          <Button variant="primary" onClick={openAdd}>
            <i className="fa fa-plus me-1" /> Add Role
          </Button>
        </div>
        <div className="table-responsive">
          <Table
            className="table-striped"
            style={{ overflowX: "auto" }}
            columns={columns}
            dataSource={data}
            rowKey={(record) => record.id}
            loading={loading}
          />
        </div>
      </div>

      {/* Add / Edit Role Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Form onSubmit={handleSave}>
          <Modal.Header closeButton>
            <Modal.Title>{editingId ? "Edit Role" : "Add Role"}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>
                Role Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. HR Manager"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                type="text"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                placeholder="Optional"
              />
            </Form.Group>

            <Form.Label>Module access</Form.Label>
            {editingId === 1 ? (
              <p className="text-muted">
                Super Admin always has access to every module.
              </p>
            ) : (
              <Row>
                {catalog.map((m) => (
                  <Col xs={6} key={m.key}>
                    <Form.Check
                      type="checkbox"
                      id={`mod-${m.key}`}
                      label={m.label}
                      checked={form.modules.includes(m.key)}
                      onChange={() => toggleModule(m.key)}
                      className="mb-2"
                    />
                  </Col>
                ))}
              </Row>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? "Saving..." : editingId ? "Save" : "Create"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {isDeleteModalVisible && (
        <DeleteModal
          onConfirm={handleDelete}
          onCancel={() => setDeleteModalVisible(false)}
          Name="Delete"
        />
      )}
    </div>
  );
};

export default RolesTable;
